import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, Bell, ChevronDown, LogOut, User, 
  LayoutDashboard, Wallet, ArrowUpRight, 
  TrendingUp, PieChart
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getNotifications, markAllNotificationsRead } from '../services/api';

const Navbar = () => {
  const { user, isAuthenticated, loading, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const profileRef = useRef(null);
  const notifRef = useRef(null);

  // Derive unread count from actual notification data — single source of truth
  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchNotifications();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setIsNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const fetchNotifications = async () => {
    try {
      const response = await getNotifications();
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  // When notification panel opens, mark all unread as read
  useEffect(() => {
    if (isNotifOpen && unreadCount > 0) {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      markAllNotificationsRead().catch(() => {});
    }
  }, [isNotifOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Navigation links for logged out users
  const publicNavLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
  ];

  // Navigation links for logged in users
  const authNavLinks = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Deposit', path: '/deposit', icon: Wallet },
    { name: 'Withdraw', path: '/withdraw', icon: ArrowUpRight },
    { name: 'Portfolio', path: '/portfolio', icon: PieChart },
  ];

  const userDropdownLinks = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Deposit', path: '/deposit', icon: Wallet },
    { name: 'Withdraw', path: '/withdraw', icon: ArrowUpRight },
    { name: 'Portfolio', path: '/portfolio', icon: PieChart },
    { name: 'Profile', path: '/profile', icon: User },
  ];

  const isActive = (path) => location.pathname === path;

  // Don't render until we know auth state to prevent flicker
  if (loading) {
    return (
      <nav className="fixed top-0 left-0 right-0 z-50">
        <div className="glass-card mx-4 mt-4 rounded-2xl border border-white/5">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-gray-900" />
                </div>
                <span className="text-xl font-bold font-['Outfit'] gradient-text hidden sm:block">
                  MetaBlockify
                </span>
              </div>
              <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      <div className="mx-3 sm:mx-4 mt-3 sm:mt-4 rounded-2xl border border-white/[0.06] bg-[#070b14]/95 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.4)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link 
              to={isAuthenticated && user ? "/dashboard" : "/"} 
              className="flex items-center gap-2.5" 
              data-testid="logo-link"
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-gray-900" />
              </div>
              <span className="text-lg font-bold font-['Outfit'] gradient-text hidden sm:block">
                MetaBlockify
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {isAuthenticated && user ? (
                // Logged In Navigation
                authNavLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`nav-link flex items-center gap-2 ${isActive(link.path) ? 'active' : ''}`}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    <link.icon className="w-4 h-4" />
                    {link.name}
                  </Link>
                ))
              ) : (
                // Logged Out Navigation
                publicNavLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                ))
              )}
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2 sm:gap-3">
              {isAuthenticated && user ? (
                <>
                  {/* Notifications */}
                  <div className="relative" ref={notifRef}>
                    <button
                      onClick={() => setIsNotifOpen(!isNotifOpen)}
                      className="relative p-2 rounded-xl hover:bg-white/[0.06] transition-colors"
                      data-testid="notifications-btn"
                    >
                      <Bell className="w-5 h-5 text-slate-400" />
                      {unreadCount > 0 && (
                        <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-cyan-500 rounded-full text-[10px] flex items-center justify-center text-gray-900 font-bold ring-2 ring-[#070b14]">
                          {unreadCount}
                        </span>
                      )}
                    </button>
                    
                    <AnimatePresence>
                      {isNotifOpen && (
                        <>
                          {/* Backdrop overlay — visible on all sizes */}
                          <motion.div 
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[60] bg-black/50 sm:bg-transparent" 
                            onClick={() => setIsNotifOpen(false)} 
                          />
                          <motion.div
                            initial={{ opacity: 0, y: 8, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 8, scale: 0.97 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                            className="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 top-20 sm:top-auto sm:mt-2 w-auto sm:w-80 rounded-xl overflow-hidden z-[70] bg-[#0a0f1a] border border-white/[0.1] shadow-[0_8px_40px_rgba(0,0,0,0.7)]"
                          >
                            <div className="px-4 py-3 border-b border-white/[0.08] flex items-center justify-between bg-[#0e1422]">
                              <h3 className="font-semibold text-white text-sm">Notifications</h3>
                              <span className="text-xs text-slate-500">{notifications.length} total</span>
                            </div>
                            <div className="max-h-80 overflow-y-auto">
                              {notifications.length === 0 ? (
                                <div className="p-6 text-center">
                                  <Bell className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                                  <p className="text-slate-500 text-sm">No notifications</p>
                                </div>
                              ) : (
                                notifications.slice(0, 8).map((notif) => (
                                  <div
                                    key={notif.id}
                                    className="px-4 py-3 border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors"
                                    data-testid={`notification-${notif.id}`}
                                  >
                                    <p className="font-medium text-white text-sm leading-snug break-words">{notif.title}</p>
                                    <p className="text-slate-400 text-xs mt-1 leading-relaxed break-words line-clamp-2">{notif.message}</p>
                                  </div>
                                ))
                              )}
                            </div>
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Profile Dropdown */}
                  <div className="relative" ref={profileRef}>
                    <button
                      onClick={() => setIsProfileOpen(!isProfileOpen)}
                      className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors"
                      data-testid="profile-dropdown-btn"
                    >
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
                        <span className="text-gray-900 font-bold text-sm">
                          {user?.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-slate-300 font-medium text-sm hidden sm:block">
                        {user?.name?.split(' ')[0]}
                      </span>
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform hidden sm:block ${isProfileOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {isProfileOpen && (
                        <>
                          <motion.div 
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[60] bg-black/50 sm:bg-transparent" 
                            onClick={() => setIsProfileOpen(false)} 
                          />
                          <motion.div
                            initial={{ opacity: 0, y: 8, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 8, scale: 0.97 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                            className="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 top-20 sm:top-auto sm:mt-2 w-auto sm:w-56 rounded-xl overflow-hidden z-[70] bg-[#0a0f1a] border border-white/[0.1] shadow-[0_8px_40px_rgba(0,0,0,0.7)]"
                          >
                            <div className="px-4 py-3 border-b border-white/[0.08] bg-[#0e1422]">
                              <p className="font-semibold text-white text-sm">{user?.name}</p>
                              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                              <div className="mt-2 flex items-center gap-2">
                                <span className={`badge ${user?.tier === 'VIP' ? 'badge-warning' : 'badge-info'}`}>
                                  {user?.tier}
                                </span>
                              </div>
                            </div>
                            <div className="p-1.5">
                              {userDropdownLinks.map((link) => (
                                <Link
                                  key={link.path}
                                  to={link.path}
                                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.06] transition-colors text-slate-300 hover:text-white text-sm"
                                  onClick={() => setIsProfileOpen(false)}
                                >
                                  <link.icon className="w-4 h-4" />
                                  {link.name}
                                </Link>
                              ))}
                              <div className="border-t border-white/[0.06] mt-1 pt-1">
                                <button
                                  onClick={handleLogout}
                                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-red-500/10 transition-colors text-red-400 text-sm"
                                  data-testid="logout-btn"
                                >
                                  <LogOut className="w-4 h-4" />
                                  Logout
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                </>
              ) : (
                // Logged Out - Auth Buttons
                <div className="flex items-center gap-3">
                  <Link
                    to="/login"
                    className="btn-secondary hidden sm:block"
                    data-testid="login-btn"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="btn-primary"
                    data-testid="register-btn"
                  >
                    Start Trading
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/5"
                data-testid="mobile-menu-btn"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden overflow-hidden border-t border-white/5"
            >
              <div className="p-4 space-y-2">
                {isAuthenticated && user ? (
                  // Logged In Mobile Menu
                  <>
                    {authNavLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                          isActive(link.path) ? 'bg-cyan-500/10 text-cyan-400' : 'text-slate-300 hover:bg-white/5'
                        }`}
                      >
                        <link.icon className="w-4 h-4" />
                        {link.name}
                      </Link>
                    ))}
                    <div className="border-t border-white/5 my-2 pt-2">
                      <Link
                        to="/profile"
                        className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5"
                      >
                        <User className="w-4 h-4" />
                        Profile
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </>
                ) : (
                  // Logged Out Mobile Menu
                  <>
                    {publicNavLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`block px-4 py-3 rounded-lg ${
                          isActive(link.path) ? 'bg-cyan-500/10 text-cyan-400' : 'text-slate-300 hover:bg-white/5'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                    <div className="border-t border-white/5 my-2 pt-2 space-y-2">
                      <Link
                        to="/login"
                        className="block px-4 py-3 rounded-lg text-slate-300 hover:bg-white/5 text-center"
                      >
                        Login
                      </Link>
                      <Link
                        to="/register"
                        className="block btn-primary text-center"
                      >
                        Start Trading
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;
